import { createContext, useReducer, useContext, useCallback, Dispatch, ReactNode } from 'react';
import { message } from 'antd';
import { getReferralCount, getCurrentPlan, getCurrentPlanByInviteCode, getShareInfo, getWalletMinAmount, getWalletList } from '@/service/referral';
import { checkBlacklist } from '@/service/vip';
import type { ICurrentPlanInfo, IShareInfo, IUserPointsInfo, IUserWalletInfo } from '@/components/Referral/index.d';

interface ReferralState {
    // 活动状态， -1 活动前、1 活动中、0 活动结束
    actStatus: null | -1 | 1 | 0;
    // 是否在黑名单中
    inBlackList: boolean;
    userPointsInfo: IUserPointsInfo | null;
    // 用户钱包信息
    userWalletInfo: IUserWalletInfo | null;
    // 基础信息
    referralInfo: ICurrentPlanInfo | null;
    // 分享信息
    shareInfo: IShareInfo | null;
    // 正在使用页面信息
    pageUsing?: 'dp3' | '';
}

type ReferralAction = {
    type: string;
    payload: any;
};

const initState: ReferralState = {
    actStatus: null,
    inBlackList: false,
    userPointsInfo: null,
    userWalletInfo: null,
    referralInfo: null,
    shareInfo: null,
    pageUsing: undefined,
};

const fsReducer = (state: ReferralState, action: ReferralAction): ReferralState => {
    const { type, payload } = action;
    switch (type) {
        case 'changeIsActivity':
            return { ...state, actStatus: payload };
        case 'changeBlackList':
            return { ...state, inBlackList: payload };
        case 'changeUserPointsInfo':
            return { ...state, userPointsInfo: payload };
        case 'changeWalletInfo':
            return { ...state, userWalletInfo: payload };
        case 'changeReferralInfo':
            return { ...state, referralInfo: payload };
        case 'changeShareInfo':
            return { ...state, shareInfo: payload };
        case 'changePageUsingInfo':
            return { ...state, pageUsing: payload };
        default:
            return state;
    }
};

export const ReferralContext = createContext<{ state: ReferralState; dispatch: Dispatch<ReferralAction> }>({
    state: initState,
    dispatch: () => null,
});

const asyncDispatch = (dispatch: Dispatch<ReferralAction>) =>
    useCallback(async ({ type, payload }: ReferralAction) => {
        switch (type) {
            case 'getUserPointsInfo':
                try {
                    const res = await getReferralCount(payload.locale, payload.planId);
                    if (res.code !== '0') return message.error(res.message);
                    dispatch({
                        type: 'changeUserPointsInfo',
                        payload: res.data,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'getUserWalletInfo':
                try {
                    const minAmount = await getWalletMinAmount(payload.locale);
                    const wallet = await getWalletList(payload.locale);
                    if (minAmount.code !== '0') return message.error(minAmount.message);
                    if (wallet.code !== '0') return message.error(wallet.message);
                    // wallet是数组 和后端约定默认取第一个
                    const data = Object.assign(minAmount.data, wallet.data[0] || {});
                    dispatch({
                        type: 'changeWalletInfo',
                        payload: data,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'getReferralInfo':
                try {
                    const res = await getCurrentPlan(payload.locale, payload.path);
                    // 检测是否在黑名单中
                    const black = await checkBlacklist(payload.locale);
                    // res.data.inBlackList = true;
                    if (res.code === '2439') {
                        location.href = `/${payload.locale}/404`;
                        return;
                    }
                    if (res.code !== '0') return message.error(res.message);
                    dispatch({
                        type: 'changeReferralInfo',
                        payload: res.data || {},
                    });
                    dispatch({
                        type: 'changeBlackList',
                        payload: black.data.inBlackList,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;

            case 'getReferralInfoByInviteCode':
                try {
                    const res = await getCurrentPlanByInviteCode(payload.locale, payload.inviteCode);
                    // 检测是否在黑名单中
                    const black = await checkBlacklist(payload.locale);
                    // res.data.inBlackList = true;
                    if (res.code === '2439') {
                        location.href = `/${payload.locale}/404`;
                        return;
                    }
                    if (res.code !== '0') return message.error(res.message);
                    dispatch({
                        type: 'changeReferralInfo',
                        payload: res.data || {},
                    });
                    dispatch({
                        type: 'changeBlackList',
                        payload: black.data.inBlackList,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            case 'getShareInfo':
                try {
                    const res = await getShareInfo(payload.data, payload.deviceId, payload.locale);
                    if (res.code !== '0') return message.error(res.message);
                    dispatch({
                        type: 'changeShareInfo',
                        payload: res.data,
                    });
                } catch (err) {
                    console.log(err);
                }
                return;
            default:
                dispatch({ type, payload });
        }
    }, []);

export function ReferralProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(fsReducer, initState);
    return <ReferralContext.Provider value={{ state, dispatch: asyncDispatch(dispatch) }}>{children}</ReferralContext.Provider>;
}

export const useReferralStore = () => {
    const store = useContext(ReferralContext);
    return store.state;
};

export const useReferralDispatch = () => {
    const store = useContext(ReferralContext);
    return store.dispatch;
};
